import styled from "styled-components";
import FormRow from "../../shared/form-row/form-row";
import {
  CommuteSettingsValidationErrors,
  WorkingTemplateCommuteSettingsDraft
} from "./commute-settings.model";

const RECOGNITION_FIELDS = [
  "checkinAdjustUse",
  "checkinAdjustUnitMin",
  "checkinAdjustEndTime",
  "checkinFixedEndUse",
  "checkinFixedEndStartTime",
  "checkinFixedEndTime",
  "checkinLimitUse",
  "checkinLimitTime"
] as const;

const recognitionHeadingColor = "#087f79";
const recognitionMutedColor = "#445f76";
const recognitionErrorColor = "#b42318";

type RecognitionField = (typeof RECOGNITION_FIELDS)[number];

export type CommuteRecognitionSettingsValue = Pick<
  WorkingTemplateCommuteSettingsDraft,
  RecognitionField
>;

export interface CommuteRecognitionSettingsProps {
  value: CommuteRecognitionSettingsValue;
  onChange: (
    field: RecognitionField,
    value: CommuteRecognitionSettingsValue[RecognitionField]
  ) => void;
  errors?: Pick<CommuteSettingsValidationErrors, RecognitionField>;
  idPrefix?: string;
}

const RecognitionBox = styled.fieldset`
  display: grid;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin: 10px 0;
  padding: 14px 16px;
  border: 1px solid ${props => props.theme.colors.frameGrey};
  border-radius: 5px;
  background: ${props => props.theme.colors.lightGrey};

  > legend {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

const Group = styled.section`
  display: grid;
  min-width: 0;
  gap: 8px;

  & + & {
    margin-top: 14px;
  }

  h5 {
    margin: 0;
    color: ${recognitionHeadingColor};
    font-size: 13px;
    font-weight: 700;
  }
`;

const Line = styled.div<{ $indent?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  gap: 7px;
  padding-left: ${props => (props.$indent ? "20px" : "0")};

  > label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  input[type="checkbox"] {
    accent-color: ${props => props.theme.colors.darkGreen};
  }

  input[type="number"] {
    width: 72px;
  }

  input[type="time"] {
    width: 105px;
  }

  input:focus-visible {
    outline: 2px solid ${props => props.theme.colors.darkBlue};
    outline-offset: 2px;
  }

  input:disabled {
    color: ${props => props.theme.colors.darkGrey};
    background: ${props => props.theme.colors.littleLightGrey};
    cursor: not-allowed;
    opacity: 0.72;
  }
`;

const Hint = styled.span`
  color: ${recognitionMutedColor};
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

const ErrorMessage = styled.span`
  flex-basis: 100%;
  color: ${recognitionErrorColor};
  line-height: 1.4;
`;

interface FieldProps {
  id: string;
  label: string;
  error?: string;
  children: (describedBy: string | undefined) => React.ReactNode;
}

function Field({ id, label, error, children }: FieldProps) {
  const errorId = `${id}-error`;
  return (
    <>
      <label htmlFor={id}>{label}</label>
      {children(error ? errorId : undefined)}
      {error && (
        <ErrorMessage id={errorId} role="alert">
          {error}
        </ErrorMessage>
      )}
    </>
  );
}

export function CommuteRecognitionSettings({
  value,
  onChange,
  errors = {},
  idPrefix = "commute-recognition"
}: CommuteRecognitionSettingsProps) {
  const id = (field: RecognitionField) => `${idPrefix}-${field
    .replace(/([A-Z])/g, "-$1")
    .toLowerCase()}`;

  return (
    <FormRow title="출근 인정시간 설정" gridNumber={1} minHeight="auto">
      <RecognitionBox aria-label="출근 인정시간 설정">
        <legend>출근 인정시간 설정</legend>
        <Group>
          <h5>출근 체크시간 보정</h5>
          <Line>
            <label htmlFor={id("checkinAdjustUse")}>
              <input
                id={id("checkinAdjustUse")}
                type="checkbox"
                checked={value.checkinAdjustUse === 1}
                onChange={event =>
                  onChange("checkinAdjustUse", event.currentTarget.checked ? 1 : 0)
                }
              />
              출근 체크시간 보정 사용
            </label>
            <Hint>미사용 시 기존 출근 체크시간을 그대로 사용합니다.</Hint>
          </Line>
          <Line $indent>
            <Field
              id={id("checkinAdjustUnitMin")}
              label="보정 단위"
              error={errors.checkinAdjustUnitMin}
            >
              {describedBy => (
                <input
                  id={id("checkinAdjustUnitMin")}
                  type="number"
                  min={1}
                  value={value.checkinAdjustUnitMin}
                  disabled={value.checkinAdjustUse === 0}
                  aria-invalid={Boolean(errors.checkinAdjustUnitMin)}
                  aria-describedby={describedBy}
                  onChange={event =>
                    onChange(
                      "checkinAdjustUnitMin",
                      Number(event.currentTarget.value)
                    )
                  }
                />
              )}
            </Field>
            <span>분 단위 올림</span>
            <Field
              id={id("checkinAdjustEndTime")}
              label="보정 적용 종료시간"
              error={errors.checkinAdjustEndTime}
            >
              {describedBy => (
                <input
                  id={id("checkinAdjustEndTime")}
                  type="time"
                  value={value.checkinAdjustEndTime}
                  disabled={value.checkinAdjustUse === 0}
                  aria-invalid={Boolean(errors.checkinAdjustEndTime)}
                  aria-describedby={describedBy}
                  onChange={event =>
                    onChange("checkinAdjustEndTime", event.currentTarget.value)
                  }
                />
              )}
            </Field>
            <Hint>
              예: 07:01→07:15, 07:16→07:30 / 11시 이후는 실제 체크시간 사용
            </Hint>
          </Line>
        </Group>

        <Group>
          <h5>특정 시간 이후 퇴근 계획시간 고정</h5>
          <Line>
            <label htmlFor={id("checkinFixedEndUse")}>
              <input
                id={id("checkinFixedEndUse")}
                type="checkbox"
                checked={value.checkinFixedEndUse === 1}
                onChange={event =>
                  onChange(
                    "checkinFixedEndUse",
                    event.currentTarget.checked ? 1 : 0
                  )
                }
              />
              고정 퇴근 계획시간 사용
            </label>
          </Line>
          <Line $indent>
            <Field
              id={id("checkinFixedEndStartTime")}
              label="출근 인정시간"
              error={errors.checkinFixedEndStartTime}
            >
              {describedBy => (
                <input
                  id={id("checkinFixedEndStartTime")}
                  type="time"
                  value={value.checkinFixedEndStartTime}
                  disabled={value.checkinFixedEndUse === 0}
                  aria-invalid={Boolean(errors.checkinFixedEndStartTime)}
                  aria-describedby={describedBy}
                  onChange={event =>
                    onChange(
                      "checkinFixedEndStartTime",
                      event.currentTarget.value
                    )
                  }
                />
              )}
            </Field>
            <span>이후 출근 시</span>
            <Field
              id={id("checkinFixedEndTime")}
              label="퇴근 계획시간"
              error={errors.checkinFixedEndTime}
            >
              {describedBy => (
                <input
                  id={id("checkinFixedEndTime")}
                  type="time"
                  value={value.checkinFixedEndTime}
                  disabled={value.checkinFixedEndUse === 0}
                  aria-invalid={Boolean(errors.checkinFixedEndTime)}
                  aria-describedby={describedBy}
                  onChange={event =>
                    onChange("checkinFixedEndTime", event.currentTarget.value)
                  }
                />
              )}
            </Field>
            <span>으로 설정</span>
          </Line>
        </Group>

        <Group>
          <h5>출근 체크 가능시간 제한</h5>
          <Line>
            <label htmlFor={id("checkinLimitUse")}>
              <input
                id={id("checkinLimitUse")}
                type="checkbox"
                checked={value.checkinLimitUse === 1}
                onChange={event =>
                  onChange("checkinLimitUse", event.currentTarget.checked ? 1 : 0)
                }
              />
              출근 체크 가능시간 제한 사용
            </label>
          </Line>
          <Line $indent>
            <Field
              id={id("checkinLimitTime")}
              label="출근 체크 가능시간"
              error={errors.checkinLimitTime}
            >
              {describedBy => (
                <input
                  id={id("checkinLimitTime")}
                  type="time"
                  value={value.checkinLimitTime}
                  disabled={value.checkinLimitUse === 0}
                  aria-invalid={Boolean(errors.checkinLimitTime)}
                  aria-describedby={describedBy}
                  onChange={event =>
                    onChange("checkinLimitTime", event.currentTarget.value)
                  }
                />
              )}
            </Field>
            <span>까지</span>
            <Hint>
              예: 20:00까지 출근 체크 가능, 20:00 초과는 출근 처리하지 않음
            </Hint>
          </Line>
        </Group>
      </RecognitionBox>
    </FormRow>
  );
}

export default CommuteRecognitionSettings;
